import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import {
    FieldInterventionControl,
    JsonForm,
    JsonSchemaFormProperty,
    UiSchemaCategory,
    UiSchemaCategoryElementsInner,
    UiSchemaControl,
} from '@connect-services/client/sdk/api';

export interface FieldInterventionControlsCategory {
    category: string;
    groupedControls: FieldInterventionControlsGroup[];
}

export interface FieldInterventionControlsGroup {
    controls: JsonSchemaFieldInterventionFormControl[];
    title: string;
}

export interface FieldInterventionControlsCategoryWithKey extends FieldInterventionControlsCategory {
    groupedControls: FieldInterventionControlsGroupWithKey[];
    key: string;
}

export interface FieldInterventionControlsGroupWithKey extends FieldInterventionControlsGroup {
    key: string;
}

export enum InputTypeEnum {
    DATE = 'date',
    DROP_DOWN_MULTIPLE_ANSWER = 'dropDownMultipleAnswer',
    DROP_DOWN_UNIQUE_ANSWER = 'dropDownUniqueAnswer',
    NUMBER = 'number',
    PHOTOS = 'photos',
    TEXT_FIELD = 'textField',
    YES_NO_ANSWER = 'yesNoAnswer',
}

export type JsonSchemaFieldInterventionFormControl = JsonSchemaFormProperty & {
    id: string;
    inputType: InputTypeEnum;
    isArray?: boolean;
    required: boolean;
    resolvedDescription?: string;
    resolvedTitle?: string;
};

@Injectable({
    providedIn: 'root',
})
export class JsonFormService {
    private readonly translocoService = inject(TranslocoService);

    public fieldInterventionGroupControlsByCategory(jsonForm: JsonForm): FieldInterventionControlsCategory[] {
        if (!jsonForm) {
            return [];
        }

        return jsonForm.uiSchema.elements.map(
            (uiSchemaCategory: UiSchemaCategory): FieldInterventionControlsCategory => {
                const controlsByGroup: FieldInterventionControlsGroup[] = [];
                const controlsWithoutGroup: JsonSchemaFieldInterventionFormControl[] = [];

                uiSchemaCategory.elements.forEach(
                    (uiSchemaCategoryElementsInner: UiSchemaCategoryElementsInner): void => {
                        if ('elements' in uiSchemaCategoryElementsInner) {
                            const controlGroups: FieldInterventionControlsGroup = {
                                controls: [],
                                title: uiSchemaCategoryElementsInner.label,
                            };
                            controlGroups.controls = uiSchemaCategoryElementsInner.elements.map(
                                (uiSchemaControl: UiSchemaControl): JsonSchemaFieldInterventionFormControl =>
                                    this.buildJsonFieldInterventionFormControl(uiSchemaControl, jsonForm),
                            );
                            controlsByGroup.push(controlGroups);

                            return;
                        }

                        controlsWithoutGroup.push(
                            this.buildJsonFieldInterventionFormControl(uiSchemaCategoryElementsInner, jsonForm),
                        );
                    },
                );

                if (controlsWithoutGroup.length) {
                    controlsByGroup.push({
                        controls: [...controlsWithoutGroup],
                        title: this.translocoService.translate('other', { COUNT: controlsWithoutGroup.length }),
                    });
                }

                return {
                    category: uiSchemaCategory.label,
                    groupedControls: controlsByGroup,
                };
            },
        );
    }

    private buildJsonFieldInterventionFormControl(
        control: UiSchemaControl,
        jsonForm: JsonForm,
    ): JsonSchemaFieldInterventionFormControl {
        const controlId = this.getControlId(control);
        const schema = jsonForm.jsonSchema.properties[controlId];
        const isArray = schema.type === JsonSchemaFormProperty.TypeEnum.ARRAY;
        const fallbackTitle = isArray ? schema.items?.title : undefined;
        const fallbackDesc = isArray ? schema.items?.description : undefined;

        const displayTitle = schema.title?.trim() || fallbackTitle?.trim() || '-';
        const displayDescription = schema.description?.trim() || fallbackDesc?.trim() || undefined;

        return {
            ...jsonForm.jsonSchema.properties[controlId],
            id: controlId,
            inputType: this.getFieldInterventionControlType(jsonForm.jsonSchema.properties[controlId]),
            isArray: isArray,
            required: this.getIsRequired(controlId, jsonForm),
            resolvedDescription: displayDescription,
            resolvedTitle: displayTitle,
        };
    }

    private getControlId(control: UiSchemaControl): string {
        const regex = new RegExp('^#\\/properties\\/[\\w-]+$');
        const isScopeCorrect = regex.test(control.scope);
        if (!isScopeCorrect) {
            throw Error('Wrong control scope format');
        }

        return control.scope.split('/').at(2)!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
    }

    private getFieldInterventionControlType(control: JsonSchemaFormProperty): InputTypeEnum {
        switch (control.type) {
            case JsonSchemaFormProperty.TypeEnum.ARRAY: {
                if (control.items?.format === FieldInterventionControl.FormatEnum.URI) {
                    return InputTypeEnum.PHOTOS;
                }
                if (control.items?.enumeration) {
                    return InputTypeEnum.DROP_DOWN_MULTIPLE_ANSWER;
                }
                break;
            }
            case JsonSchemaFormProperty.TypeEnum.NUMBER:
            case JsonSchemaFormProperty.TypeEnum.INTEGER:
                return InputTypeEnum.NUMBER;
            case JsonSchemaFormProperty.TypeEnum.STRING: {
                if (control.format === FieldInterventionControl.FormatEnum.URI) {
                    return InputTypeEnum.PHOTOS;
                }
                if (control.enumeration) {
                    return InputTypeEnum.DROP_DOWN_UNIQUE_ANSWER;
                }
                if (
                    control.format === FieldInterventionControl.FormatEnum.DATE ||
                    control.format === FieldInterventionControl.FormatEnum.DATE_TIME
                ) {
                    return InputTypeEnum.DATE;
                }
                break;
            }
            case JsonSchemaFormProperty.TypeEnum.BOOLEAN:
                return InputTypeEnum.YES_NO_ANSWER;
        }

        return InputTypeEnum.TEXT_FIELD;
    }

    private getIsRequired(controlId: string, jsonForm: JsonForm): boolean {
        return jsonForm.jsonSchema.required.includes(controlId);
    }
}
